import { useQuery } from '@tanstack/react-query';
import Button from '@/components/ui/Button';
import api from '@/services/api';

interface AccountingSummary {
  totalAssets: number;
  totalLiabilities: number;
  totalEquity: number;
  totalRevenue: number;
  totalExpenses: number;
  netProfit: number;
  cashBalance: number;
  totalTransactions: number;
}

export default function AccountingPage() {
  const { data: summary = {} as AccountingSummary } = useQuery<AccountingSummary>({
    queryKey: ['accounting', 'summary'],
    queryFn: async () => {
      const res = await api.get('/api/accounting/summary');
      return res.data.data;
    },
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-MW', {
      style: 'currency',
      currency: 'MWK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Accounting</h1>
        <p className="text-gray-600">Manage your financial records and reports</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500">Total Revenue</h3>
          <p className="text-2xl font-bold text-green-600">
            {summary ? formatCurrency(summary.totalRevenue) : 'MWK 0'}
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500">Total Expenses</h3>
          <p className="text-2xl font-bold text-red-600">
            {summary ? formatCurrency(summary.totalExpenses) : 'MWK 0'}
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500">Net Profit</h3>
          <p className="text-2xl font-bold text-blue-600">
            {summary ? formatCurrency(summary.netProfit) : 'MWK 0'}
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500">Total Transactions</h3>
          <p className="text-2xl font-bold text-gray-900">
            {summary?.totalTransactions || 0}
          </p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white p-6 rounded-lg shadow mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Button 
            variant="primary"
            onClick={() => {
              // Navigate to invoice creation or open modal
              console.log('Create Invoice clicked');
              alert('Invoice creation feature coming soon!');
            }}
          >
            Create Invoice
          </Button>
          <Button 
            variant="ghost"
            onClick={() => {
              // Navigate to expense recording
              console.log('Record Expense clicked');
              window.location.href = '/expense-proof';
            }}
          >
            Record Expense
          </Button>
          <Button 
            variant="ghost"
            onClick={() => {
              // Navigate to report generation
              console.log('Generate Report clicked');
              alert('Report generation feature coming soon!');
            }}
          >
            Generate Report
          </Button>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Transactions</h2>
        <div className="text-gray-500 text-center py-8">
          No recent transactions found
        </div>
      </div>
    </div>
  );
};

